import { Component, EventEmitter, HostBinding, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { pageAnimation } from '@app/core/animations/element.animations';
import { getInvalidFieldMessage, getRequiredFieldMessage, login403Error } from '@app/user-on-boarding/shared/constants';
import { AuthService } from '@app/services/auth.service';
import { EventService } from '@app/shared/events/event.service';
import { Store } from '@ngrx/store';
import * as fromApp from '../../store/app.reducers';
import { LoginAttempt, ClearLoginError } from '../../store/auth/auth.actions';
import { AuthEvents } from '../../store/auth/models/events/auth.event.model';
import { of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil, timeout } from 'rxjs/operators';
import { SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-shared-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [pageAnimation]
})
export class LoginComponent implements OnInit, OnDestroy {

    @HostBinding('@pageAnimation') animate = true;

    form: UntypedFormGroup;
    readonly = true;
    submitted = false;
    unknownErrorMessages = [];
    isAuthenticating = false;
    isFromSetPassword = false;
    showPassword = false;
    @Input() loginError: SafeHtml;
    @Input() isModal = false;
    @Output() close = new EventEmitter();

    private destroy$ = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        private auth: AuthService,
        private eventService: EventService,
        private router: Router,
        private route: ActivatedRoute,
        private store: Store<fromApp.AppState>) {
    }

    /**
    * Marks all controls in a form group as touched
    * @param formGroup - The form group to touch
    */
    private markFormGroupDirtyTouched(formGroup: UntypedFormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            formGroup.controls[key].markAsDirty();
            formGroup.controls[key].updateValueAndValidity();
        }
    }

    ngOnInit() {
        this.createForm();

        // Clear any existing login errors when component initializes
        this.store.dispatch(new ClearLoginError());

        if (this.isModal) {
            const currentPath = window.location.pathname;
            this.auth.handleAuthenticationURLEntry(currentPath);
        }

        // Listen to auth state changes to handle successful login
        this.store.select(fromApp.getLoggedIn).pipe(
            takeUntil(this.destroy$),
            filter(loggedIn => loggedIn && this.isAuthenticating) // Only act when we're in the middle of authentication
        ).subscribe(() => {
            const entryUrl = this.auth.getAuthenticationURLEntry();

            // We intentionally do a full navigation after login (window.location.replace)
            // because this ensures user state is refreshed (eg. CXM-2649).
            // This cancels in-flight HTTP requests, so to ensure auth:login is captured,
            // send it immediately and wait briefly for completion (silent fail on timeout/error).
            this.eventService.logImmediate(AuthEvents.login({ platform: 'mylab-web' })).pipe(
                take(1),
                timeout(2000),
                catchError(() => of(null))
            ).subscribe(() => {
                this.isAuthenticating = false;
                if (this.isModal) {
                    this.close.emit();
                } else {
                    window.location.replace(entryUrl);
                }
            });
        });

        // Listen to login errors from NgRx store to handle authentication state
        this.store.select(fromApp.getLoginError).pipe(
            takeUntil(this.destroy$),
            filter(error => error && this.isAuthenticating) // Only act when we're in the middle of authentication
        ).subscribe((error) => {
            this.isAuthenticating = false;
            this.markFormGroupDirtyTouched(this.form);
            // Don't set unknownErrorMessages here - let the input property handle display
            // This subscription is only for stopping the authentication process
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    createForm() {
        this.form = this.fb.group({ // <-- the parent FormGroup
            email: [null, [ Validators.email, Validators.required]],
            password: [null, Validators.required ]
        });

        const queryParams = this.route.snapshot.queryParams;

        if (queryParams.email && queryParams.success && queryParams.message) {
            this.isFromSetPassword = queryParams.email && queryParams.success && queryParams.message;
            this.form.controls.email.setValue(queryParams.email);
        }
    }

    onLogin() {
        this.unknownErrorMessages = [];

        this.submitted = true;

        if (!this.form.valid) {
            this.markFormGroupDirtyTouched(this.form);
            return;
        }

        this.isAuthenticating = true;
        this.unknownErrorMessages = [];

        // Dispatch login attempt action - effects will handle the rest
        this.store.dispatch(new LoginAttempt(
            this.form.get('email').value,
            this.form.get('password').value,
            this.isModal
        ));
    }

    onCreateAccount() {
        this.router.navigate(['account', 'create']);
    }

    onForgotPassword() {
        this.router.navigate(['account', 'reset-password']);
    }

    getErrorMessage(formControlName: string) {
        const formControl = this.form.get(formControlName);
        if (formControl.hasError('required')) {
            return getRequiredFieldMessage(formControlName);
        } else if (formControl.hasError(formControlName)) {
            return getInvalidFieldMessage(formControlName);
        }
    }

    setOptInAsGuest() {
        this.auth.setOptInAsGuest();
    }
    onContinueAsGuest() {
        this.setOptInAsGuest();
        this.close.emit();
    }
}
